import { RECEIPT_NAME } from "./constants";
import {
  buildAccountingReconciliation,
  buildAuditPackageManifest,
  buildIntegrationStatus,
  buildManagerExceptionQueue,
  buildPolicyGates,
  buildProviderEvidenceSchema
} from "./policyGates";
import { formatMoney } from "./money";
import type { BuyerFundingReview } from "./types";

export function buildBuyerCollectionPacket(review: BuyerFundingReview) {
  return {
    mode: "assisted_collection_live_local",
    livePortal: true,
    accessPage: {
      title: "Buyer funding request",
      copy: `${review.officeName} is recording funding evidence for file ${review.fileNumber || "not recorded"}. This page helps the office keep the funding review in the file.`
    },
    instructionReview: {
      expectedAmount: formatMoney(review.expectedFunds.amount || review.expectedAmount),
      deadline: review.fundingInstruction.deadlineAt || review.dueAt || "Not recorded",
      paymentMethod: review.paymentRail.rail || review.fundingInstruction.allowedRails.join(", ") || "Not recorded",
      instructionVersion: review.fundingInstruction.versionLabel || "Not recorded",
      action: "I acknowledge these instructions"
    },
    evidenceRequests: [
      "Acknowledge active funding instructions",
      "Provide source account evidence or manual bank proof",
      "Upload payment receipt if payment was initiated",
      "Explain third-party, gift, entity, or trust funds when applicable",
      "Report conflicting instructions if received"
    ],
    completionCopy: `Your information was submitted to ${review.officeName}. The office will decide what action to take.`,
    boundary:
      "Source account evidence helps the office record its review. It does not guarantee final settlement or approval. The office decides."
  };
}

export function buildFeasibleSpecPackage(review: BuyerFundingReview) {
  return {
    product: RECEIPT_NAME,
    policyGates: buildPolicyGates(review),
    buyerCollectionPacket: buildBuyerCollectionPacket(review),
    accountingReconciliation: buildAccountingReconciliation(review),
    providerEvidenceSchema: buildProviderEvidenceSchema(review),
    managerExceptionQueue: buildManagerExceptionQueue(review),
    auditPackageManifest: buildAuditPackageManifest(review),
    integrationStatus: buildIntegrationStatus(),
    coverageSummary: {
      manualReceiptWorkflow: true,
      fourWayMatch: true,
      openGapsRetained: true,
      officeActionRecorded: true,
      buyerPortalEvidenceIntake: true,
      saveBackZip: true,
      livePlaid: false,
      liveCertifid: false,
      liveClosinglock: false,
      liveBankFeed: false,
      liveTpsSaveBack: false
    }
  };
}

export function buildRedactedSummary(review: BuyerFundingReview) {
  return {
    reviewNumber: review.reviewNumber,
    fileNumber: review.fileNumber,
    status: review.status,
    fundingPurpose: review.fundingPurpose,
    expectedAmount: formatMoney(review.expectedFunds.amount || review.expectedAmount),
    officeAction: review.officeAction.action,
    fundsStatus: review.fundsStatus,
    openGapCount: review.openGaps.length,
    buyerPortalSubmissions: review.buyerPortalSubmissions?.length || 0,
    trustBoundary:
      "Veto records the review. The office decides. Sensitive account values are masked in exports."
  };
}
